import { Component, computed, output, signal, inject } from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {H2Component} from '~/app/shared/components/elements/h2/h2.component';
import {H3Component} from '~/app/shared/components/elements/h3/h3.component';
import {LabelComponent} from '~/app/shared/components/elements/label/label.component';
import {IconComponent} from '~/app/shared/components/elements/icon/icon.component';
import {ApplicationFacade} from "~/app/core/state/application/application-facade/application.facade";
import {toSignal} from "@angular/core/rxjs-interop";
import {PromotedFeaturePopIn, PromotedFeaturePopInCTA} from "~/app/domains/onboarding/types/promoted-feature.type";
import {PromotedFeatureEnum} from "~/app/domains/onboarding/enums/promoted-feature.enums";
import {NewFeaturePopInService} from "~/app/domains/onboarding/services/new-feature-pop-in.service";
import {MarkdownPipe} from "~/app/shared/pipes/markdown.pipe";
import {ModalRef} from "~/app/shared/services/modal/modal-ref";
import {
    RequestCreditsModalArgs
} from "~/app/domains/explanation/modals/explanation-end-trial-orchestrator/end-trial-credits-orchestrator-modal.component";
import {PromotedFeatureActionEnum} from "~/app/domains/onboarding/enums/promoted-feature-action.enums";
import {TUTORIAL_MENU_ID} from "~/app/shared/constants/menu-item-id.constant";
import {TrackingDirective} from "~/app/shared/directives/matomo/tracking.directive";
import {ButtonComponent} from "~/app/shared/components/elements/buttons/button/button.component";
import {VgBufferingModule} from '@videogular/ngx-videogular/buffering';
import {VgControlsModule} from '@videogular/ngx-videogular/controls';
import {VgCoreModule} from '@videogular/ngx-videogular/core';
import {VgOverlayPlayModule} from '@videogular/ngx-videogular/overlay-play';
import {SafePipe} from '~/app/shared/pipes/safe/safe.pipe';
import {VideoTypes} from '~/app/shared/enums/variables';

@Component({
    selector: 'eb-onboarding-introduction',
    templateUrl: './onboarding-new-feature.component.html',
    host: { '[class.is-supademo]': 'isSupademoMode()' },
    imports: [
        TranslocoPipe,
        H2Component,
        H3Component,
        LabelComponent,
        IconComponent,
        MarkdownPipe,
        TrackingDirective,
        ButtonComponent,
        VgCoreModule,
        VgOverlayPlayModule,
        VgBufferingModule,
        VgControlsModule,
        SafePipe,
    ],
    standalone: true,
    styleUrl: './onboarding-new-feature.component.scss'
})
export class OnboardingNewFeatureComponent {
    protected readonly VideoTypes = VideoTypes;

    private ref = inject<ModalRef<RequestCreditsModalArgs | boolean, RequestCreditsModalArgs>>(ModalRef);
    private applicationFacade = inject(ApplicationFacade);
    private newFeaturePopInService = inject(NewFeaturePopInService);

    protected promotedFeaturesIds = toSignal(this.applicationFacade.emphasizedFeatures$);
    protected currentPromotedFeatureIndex = signal(0);
    protected isIframeLoaded = signal(false);

    public ctaEmitter = output<PromotedFeatureEnum>();

    protected promotedFeatures = computed(() => {
        const promotedFeaturesIds = (this.promotedFeaturesIds() ?? []).map(x => x.id);
        return this.newFeaturePopInService.getPromotedFeatures(promotedFeaturesIds);
    });

    protected popInContentList = computed<PromotedFeaturePopIn[]>(() => {
        const promotedFeatures = this.promotedFeatures();
        return promotedFeatures.map(x => x.popin).filter(x => typeof x !== "undefined");
    });

    protected currentPromotedFeature = computed(() => {
        const promotedFeatures = this.promotedFeatures();
        const currentPromotedFeatureIndex = this.currentPromotedFeatureIndex();
        if (promotedFeatures.length > 0 && currentPromotedFeatureIndex <= (promotedFeatures.length - 1)) {
            return promotedFeatures[currentPromotedFeatureIndex];
        }
        return null;
    });

    protected currentPromotedFeaturePopIn = computed(() => {
        const popInContentList = this.popInContentList();
        const currentPromotedFeatureIndex = this.currentPromotedFeatureIndex();
        if (popInContentList.length > 0 && currentPromotedFeatureIndex <= (popInContentList.length - 1)) {
            return popInContentList[currentPromotedFeatureIndex];
        }
        return null;
    })

    protected isSupademoMode = computed(() =>
        this.currentPromotedFeaturePopIn()?.video?.type === VideoTypes.SUPADEMO
    );

    get vm() {
        return this.viewModel();
    }

    protected viewModel = computed(() => ({
        currentPromotedFeatureIndex: this.currentPromotedFeatureIndex(),
        currentPromotedFeaturePopIn: this.currentPromotedFeaturePopIn(),
        currentPromotedFeature: this.currentPromotedFeature(),
        isSupademoMode: this.isSupademoMode()
    }))

    public triggerCTAByPromotedFeatureId(cta: PromotedFeaturePopInCTA) {
        if (cta) {
            switch (cta.action) {
                case PromotedFeatureActionEnum.SHOW_TUTORIALS_VIDEOS: {
                    const menu = this.applicationFacade.getMenusSnapshot()?.find(x => x.id === TUTORIAL_MENU_ID);
                    if (menu?.url) {
                        window.open(menu?.url, '_blank');
                    }
                    break;
                }
                default:
                    this.ref.close(true);
            }
        }
    }
}
